import * as React from 'react';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { ScrollLockUpdated } from '~/Utils/ScrollLock/ScrollLockUpdated';
import styles from './ScrollLock.module.css';

interface ScrollLockProps {
    isLocked: boolean;
    shouldHideMainScrollBar?: boolean;
}

const lockedClassName = styles['ScrollLock--enabled'];
const hiddenScrollBarClassName = styles['ScrollLock--hiddenScrollBar'];

export const ScrollLock: React.FC<ScrollLockProps> = props => {
    const { isLocked, shouldHideMainScrollBar } = props;

    const [scrollPosition, setScrollPosition] = React.useState<number | undefined>(undefined);
    const scrollPositionRef = React.useRef<number>();

    React.useEffect(() => {
        scrollPositionRef.current = scrollPosition;
    }, [scrollPosition]);

    React.useEffect(() => {
        const { body } = document;
        const scrollY = window.scrollY || window.pageYOffset;

        const lock = (position: number) => {
            // Calculated before adding locked class, because page might not have a scrollbar before that
            const scrollBarWidth = window.innerWidth - body.offsetWidth;

            body.classList.add(lockedClassName);
            body.style.top = `-${position}px`;

            if (shouldHideMainScrollBar) {
                body.classList.add(hiddenScrollBarClassName);
                body.style.paddingRight = `${scrollBarWidth}px`;

                MessageBroker.publish(
                    new ScrollLockUpdated({
                        isLocked: true,
                        isScrollBarVisible: false,
                        scrollBarWidth,
                    })
                );
            } else {
                MessageBroker.publish(
                    new ScrollLockUpdated({
                        isLocked: true,
                        isScrollBarVisible: true,
                    })
                );
            }
        };

        const unlock = (position: number) => {
            body.classList.remove(lockedClassName);
            body.style.top = '';

            if (shouldHideMainScrollBar) {
                body.classList.remove(hiddenScrollBarClassName);
                body.style.paddingRight = '';
            }

            MessageBroker.publish(new ScrollLockUpdated({ isLocked: false }));

            window.scrollTo(0, position);
        };

        const shouldLock = isLocked && !body.classList.contains(lockedClassName);

        if (shouldLock) {
            lock(scrollY);
            setScrollPosition(scrollY);
        } else {
            if (scrollPosition !== undefined) {
                unlock(scrollPosition);
            }

            setScrollPosition(undefined);
        }

        return () => {
            if (scrollPositionRef.current !== undefined) {
                unlock(scrollPositionRef.current);
            }
        };
    }, [shouldHideMainScrollBar, isLocked]);

    return null;
};
